/**
 * v2-top-ranking-tabs のビジネスロジック（純粋関数）
 *
 * カテゴリ別ランキングタブの Algolia multipleQueries 組み立てを
 * kd-algolia-ranking-tabs から分離。
 * .claude/rules/business/logic-separation.md に従い UI から独立させる。
 */

import {
  buildSearchParams,
  buildIndexName,
  type AlgoliaQueryParams,
} from './carousel.logic';
import type { AlgoliaCustomer } from './utils/algolia-filters';

export interface TabConfig {
  label: string;
  sortIndex: string;
  collectionId: string;
  filters: string;
  ruleContexts: string;
  listUrl: string;
}

export interface BatchQuery {
  indexName: string;
  params: AlgoliaQueryParams;
}

/**
 * タブのアクティブ／非アクティブ時に切り替える Tailwind クラスリスト。
 *
 * Liquid 側 `sections/v2-top-ranking-tabs.liquid` の初期 class 出力と
 * Lit 側 `_updateTabStates()` の DOM 操作で同じ値を使うため、ここで一元管理する。
 *
 * 注意:
 * - border-0 と border は同時に存在しないよう排他的に管理する（Tailwind の
 *   CSS 出力順序に依存させないため）。
 * - Liquid 側のクラス列を変更する場合は必ずこの定数と同期させること。
 */
export const TAB_ACTIVE_CLASSES = [
  'tw:bg-surface-inverse',
  'tw:text-text-inversed',
  'tw:border-0',
] as const;

export const TAB_INACTIVE_CLASSES = [
  'tw:bg-white',
  'tw:border',
  'tw:border-border-default',
  'tw:text-text-main',
] as const;

/**
 * 全タブ分の Algolia multipleQueries 配列を組み立てる。
 *
 * 個々のクエリ組み立ては carousel.logic.ts の buildSearchParams を流用し、
 * sortIndex / collectionId / filters / ruleContexts / displayNum をタブ設定から
 * 抽出して 1 リクエスト = N タブのバッチに束ねる。
 *
 * 全タブで displayNum を共通にすることで section schema を単純化している
 * （タブごとに件数を変えたくなった場合は TabConfig に displayNum を追加する）。
 */
export function buildBatchQueries(
  tabs: TabConfig[],
  baseIndexName: string,
  displayNum: number,
  customer: AlgoliaCustomer | null,
): BatchQuery[] {
  return tabs.map((tab) => ({
    indexName: buildIndexName(baseIndexName, tab.sortIndex),
    params: buildSearchParams(
      {
        sortIndex: tab.sortIndex,
        collectionId: tab.collectionId,
        filters: tab.filters,
        ruleContexts: tab.ruleContexts,
        displayNum,
        visibilityPolicy: 'default',
      },
      customer,
    ),
  }));
}

/**
 * Shopify の collection_id は数値で渡るケースと文字列で渡るケースがあるため、
 * どちらも文字列に正規化し、それ以外の型は空文字にフォールバックする。
 */
function normalizeIdToString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/**
 * [data-tabs-config] script タグの textContent から TabConfig[] をパース。
 * 不正な JSON / 欠損フィールドは安全側に倒し空配列を返す（セクション非表示にフォールバック）。
 */
export function parseTabsConfig(rawJson: string): TabConfig[] {
  if (!rawJson || !rawJson.trim()) return [];
  try {
    const parsed = JSON.parse(rawJson);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((t): t is Record<string, unknown> => typeof t === 'object' && t !== null)
      .map((t) => ({
        label: typeof t.label === 'string' ? t.label : '',
        sortIndex: typeof t.sortIndex === 'string' ? t.sortIndex : '',
        collectionId: normalizeIdToString(t.collectionId),
        filters: typeof t.filters === 'string' ? t.filters : '',
        ruleContexts: typeof t.ruleContexts === 'string' ? t.ruleContexts : '',
        listUrl: typeof t.listUrl === 'string' ? t.listUrl : '',
      }))
      .filter((t) => t.label.length > 0);
  } catch {
    return [];
  }
}
